<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { parseISO, format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const router = useRouter();

const data = ref(null);
const loading = ref(true);
const error = ref(false);
const hovered = ref(false);

const getCookie = name => {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? decodeURIComponent(match[2]) : null;
};

const getAuthHeaders = () => {
  const cookie = getCookie('cw_d_session_info');
  if (!cookie) return {};
  try {
    const sessionInfo = JSON.parse(cookie);
    return {
      'access-token': sessionInfo['access-token'],
      client: sessionInfo.client,
      uid: sessionInfo.uid,
    };
  } catch {
    return {};
  }
};

onMounted(async () => {
  try {
    const res = await axios.get('/api/v1/interactions/usage_summary', {
      headers: getAuthHeaders(),
    });
    data.value = res.data;
  } catch {
    error.value = true;
  } finally {
    loading.value = false;
  }
});

const used = computed(() => data.value?.billable_interactions || 0);
const total = computed(() => data.value?.token_limit || 0);
const percentage = computed(() => {
  if (!total.value) return 0;
  return Math.min(Math.round((used.value / total.value) * 100), 100);
});

const resetLabel = computed(() => {
  if (!data.value?.token_period_end) return '';
  const end = parseISO(data.value.token_period_end);
  const reset = new Date(end);
  reset.setDate(reset.getDate() + 1);
  return format(reset, "dd 'de' MMM", { locale: ptBR });
});

const circumference = 2 * Math.PI * 9;
const dashOffset = computed(
  () => circumference - (percentage.value / 100) * circumference
);

const strokeColor = computed(() => {
  if (percentage.value >= 100) return '#ef4444';
  if (percentage.value >= 80) return '#f59e0b';
  return '#3b82f6';
});

const goToReport = () => {
  router.push({ name: 'tokens_reports' });
};
</script>

<template>
  <div v-if="!loading && !error && data" class="token-widget-wrapper">
    <button type="button" class="token-widget" @click="goToReport">
      <span class="token-widget__label">TOKENS</span>
      <span class="token-widget__reset">reinicia {{ resetLabel }}</span>
      <span
        class="token-widget__donut"
        @mouseenter="hovered = true"
        @mouseleave="hovered = false"
      >
        <svg width="22" height="22" viewBox="0 0 22 22">
          <circle
            cx="11"
            cy="11"
            r="9"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            class="text-slate-200 dark:text-slate-700"
          />
          <circle
            cx="11"
            cy="11"
            r="9"
            fill="none"
            :stroke="strokeColor"
            stroke-width="3"
            stroke-linecap="round"
            :stroke-dasharray="circumference"
            :stroke-dashoffset="dashOffset"
            transform="rotate(-90 11 11)"
            style="transition: stroke-dashoffset 0.4s ease"
          />
        </svg>
        <transition name="tip">
          <span v-if="hovered" class="token-widget__tooltip">
            {{ percentage }}%
          </span>
        </transition>
      </span>
    </button>
    <div class="token-widget__divider" />
  </div>
</template>

<style scoped>
.token-widget-wrapper {
  margin: 4px 0 6px;
}

.token-widget {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 10px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  font: inherit;
  text-align: left;
}

.token-widget__divider {
  height: 1px;
  margin: 6px 4px 0;
  background: rgb(226 232 240);
}

.dark .token-widget__divider {
  background: rgb(51 65 85);
}

.token-widget:hover {
  background: rgb(241 245 249);
}

.dark .token-widget:hover {
  background: rgb(51 65 85);
}

.token-widget__label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: rgb(15 23 42);
}

.dark .token-widget__label {
  color: rgb(241 245 249);
}

.token-widget__reset {
  flex: 1;
  font-size: 11px;
  color: rgb(100 116 139);
}

.dark .token-widget__reset {
  color: rgb(148 163 184);
}

.token-widget__donut {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
}

.token-widget__tooltip {
  position: absolute;
  bottom: calc(100% + 6px);
  right: -4px;
  padding: 3px 6px;
  border-radius: 4px;
  background: rgb(15 23 42);
  color: white;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}

.token-widget__tooltip::after {
  content: '';
  position: absolute;
  top: 100%;
  right: 8px;
  border: 4px solid transparent;
  border-top-color: rgb(15 23 42);
}

.tip-enter-active,
.tip-leave-active {
  transition: opacity 0.12s ease, transform 0.12s ease;
}

.tip-enter-from,
.tip-leave-to {
  opacity: 0;
  transform: translateY(2px);
}
</style>
